import { useEffect, useLayoutEffect, useMemo } from "react";
import { View } from "react-native";
import { CommonActions } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useLocale } from "@src/packages/i18n";
import type { AccessStackParamList } from "../../types";
import { TherapyDetailContent } from "../../components/TherapyDetailSheet";
import { getFactSheetRouteBlock } from "../../components/factSheetRouteIds";
import type { FactSheetLinkTarget } from "../../components/LinkedFactSheetText";
import { getTestRouteTarget } from "./testRouteIds";
import { useSmartBack } from "../../hooks/useSmartBack";

type Props = NativeStackScreenProps<AccessStackParamList, "TherapyDetail">;

export default function TherapyDetailScreen({ route, navigation }: Props) {
  const { therapyId, returnTab } = route.params;
  const { locale } = useLocale();
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Access",
    fallbackScreen: "Therapies",
  });

  const item = useMemo(() => {
    void locale;
    return getFactSheetRouteBlock("therapy", therapyId);
  }, [therapyId, locale]);

  useEffect(() => {
    if (!item) {
      navigation.goBack();
    }
  }, [item, navigation]);

  useLayoutEffect(() => {
    if (item) {
      navigation.setOptions({ title: item.title });
    }
  }, [item, navigation]);

  const openFactSheetLink = (target: FactSheetLinkTarget) => {
    switch (target.kind) {
      case "condition":
        navigation.navigate("ConditionDetail", { conditionId: target.id });
        return;
      case "supplement":
        navigation.navigate("SupplementDetail", { supplementId: target.id });
        return;
      case "test":
        {
          const testTarget = getTestRouteTarget(target.id);
          if (testTarget.screen === "TestProductDetail") {
            navigation.navigate("TestProductDetail", testTarget.params);
            return;
          }
          navigation.navigate("TestDetail", testTarget.params);
        }
        return;
      case "therapy":
        navigation.navigate("TherapyDetail", { therapyId: target.id });
        return;
      case "medication":
        navigation.getParent()?.dispatch(
          CommonActions.navigate({
            name: "Learn",
            params: {
              screen: "MedicationDetail",
              params: { medicationId: target.id },
            },
          }),
        );
        return;
      case "procedure":
        navigation.getParent()?.dispatch(
          CommonActions.navigate({
            name: "Learn",
            params: {
              screen: "ProcedureDetail",
              params: { procedureId: target.id },
            },
          }),
        );
        return;
    }
  };

  if (!item) return null;

  return (
    <View style={{ flex: 1 }}>
      <TherapyDetailContent
        item={item}
        onClose={handleBack}
        onFindProvider={() => navigation.navigate("FindCare")}
        onOpenFactSheet={openFactSheetLink}
        skipExitAnimation
      />
    </View>
  );
}
